import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gamePath } from "../utils/slugify";
import { getAuth } from "firebase/auth";
import {
  collection,
  getDocs,
  db,
  doc,
  setDoc,
  deleteDoc,
} from "../firebase/firestore";
import { createGroupInFirestore } from "../services/yourLibrary/groupService";
import { deleteCustomGameStorageFiles } from "../services/gamePage/groupService";

import redDeadCover from "../assets/images/redDeadCover.jpg";
import noGameBackground from "../assets/images/noGameBackground.svg";
import loadingGif from "../assets/images/loading.gif";

import "../styles/manageLibrary.css";

export default function ManageLibrary() {
  const [stats, setStats] = useState({
    total: 0,
    backlog: 0,
    completed: 0,
    playing: 0,
    custom: 0,
  });

  const [libInfo, setLibInfo] = useState([]);
  const [groups, setGroups] = useState([]);
  const sortedGroups = [...groups].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, {
      sensitivity: "base",
    }),
  );
  const [currentPage, setCurrentPage] = useState(() =>
    Number(localStorage.getItem("ml_page") || 1),
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ml_groups") || "[]");
    } catch {
      return [];
    }
  });
  const [statusFilter, setStatusFilter] = useState(
    () => localStorage.getItem("ml_status") || "all",
  );
  const [searchTerm, setSearchTerm] = useState(
    () => localStorage.getItem("ml_search") || "",
  );
  const [selectedGames, setSelectedGames] = useState([]);
  const [pagnOpen, setPagnOpen] = useState(false);
  const [groupSelectionOpen, setGroupSelectionOpen] = useState(false);
  const [reorderSelectedGroupId, setReorderSelectedGroupId] = useState("");
  const [reorderList, setReorderList] = useState([]);
  const dragIndexRef = useRef(null);
  const [dragOverInfo, setDragOverInfo] = useState({ index: null, after: false });
  const groupSelectionRef = useRef(null);
  const [jumpPageInput, setJumpPageInput] = useState("1");
  const [isJumpInputActive, setIsJumpInputActive] = useState(false);
  const [sortBy, setSortBy] = useState("");
  const [sheetView, setSheetView] = useState(null); // null | "sort" | "game-info"
  const [activeSheetGame, setActiveSheetGame] = useState(null);

  function closeSheet() {
    setSheetView(null);
    setGroupTriggerOpen(false);
  }
  const [groupTriggerOpen, setGroupTriggerOpen] = useState(false);
  const [desktopGroupTriggerOpen, setDesktopGroupTriggerOpen] = useState(false);
  const [activeManagementTab, setActiveManagementTab] = useState("library");
  const [selectedGroupRows, setSelectedGroupRows] = useState(new Set());
  const [editingGroupRowId, setEditingGroupRowId] = useState(null);
  const [editingGroupRowName, setEditingGroupRowName] = useState("");
  const [groupPage, setGroupPage] = useState(1);
  const [groupPageTriggerOpen, setGroupPageTriggerOpen] = useState(false);
  const GROUPS_PER_PAGE = 10;
  const [bulkAction, setBulkAction] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 765);
  const [addToGroupModalOpen, setAddToGroupModalOpen] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [addToGroupSelected, setAddToGroupSelected] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(true);
  const [groupRemovalModalOpen, setGroupRemovalModalOpen] = useState(false);
  const [groupRemovalSelected, setGroupRemovalSelected] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [groupBulkAction, setGroupBulkAction] = useState("");

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 765);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    if (!groupSelectionOpen) return;
    function handleOutside(e) {
      if (
        groupSelectionRef.current &&
        !groupSelectionRef.current.contains(e.target)
      ) {
        setGroupSelectionOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [groupSelectionOpen]);

  useEffect(() => {
    localStorage.setItem("ml_page", currentPage);
    localStorage.setItem("ml_groups", JSON.stringify(selectedGroupIds));
    localStorage.setItem("ml_status", statusFilter);
    localStorage.setItem("ml_search", searchTerm);
  }, [currentPage, selectedGroupIds, statusFilter, searchTerm]);

  useEffect(() => {
    setReorderList(reorderGames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reorderSelectedGroupId, libInfo, groups]);

  function reorderDrag(from, to) {
    setReorderList((prev) => {
      if (from === null || from === to || from === to - 1) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      if (from < to) to--;
      next.splice(to, 0, moved);
      return next;
    });
  }

  function moveReorderGame(index, dir) {
    setReorderList((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function handleSaveReorder() {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user || !reorderGroup) return;
    const newOrderIds = reorderList.map((g) => String(g.id));
    await setDoc(
      doc(db, "users", user.uid, "groups", reorderGroup.id),
      { gameIds: newOrderIds },
      { merge: true },
    );
    setGroups((prev) =>
      prev.map((g) =>
        g.id === reorderGroup.id ? { ...g, gameIds: newOrderIds } : g,
      ),
    );
    setReorderModalOpen(false);
  }

  function handleCancelReorder() {
    setReorderList(reorderGames);
    setReorderModalOpen(false);
  }

  async function handleApply(overrideAction, overrideGames) {
    const action = overrideAction || bulkAction;
    const gamesToApply = overrideGames || selectedGames;
    if (!action || gamesToApply.length === 0) return;
    if (action === "add-to-group") {
      setAddToGroupModalOpen(true);
      return;
    }

    if (action === "remove-from-group") {
      if (gamesToApply.length === 0) return;
      setGroupRemovalModalOpen(true);
      return;
    }
    const user = getAuth().currentUser;
    if (!user) return;

    setIsApplying(true);
    try {
      if (action === "complete") {
        await Promise.all(
          gamesToApply.map(async (game) => {
            const docId = String(game.id);
            const completedRef = doc(db, "users", user.uid, "completed", docId);
            const libraryRef = doc(db, "users", user.uid, "library", docId);
            const completedAt = new Date().toISOString();
            await Promise.all([
              setDoc(completedRef, {
                ...game,
                status: "completed",
                completedAt,
              }),
              setDoc(libraryRef, {
                ...game,
                inLibrary: true,
                status: "completed",
                completedAt,
              }),
            ]);
          }),
        );
      }

      if (action === "remove") {
        const removedIds = new Set(gamesToApply.map((g) => String(g.id)));

        await Promise.all(
          gamesToApply.map(async (game) => {
            const docId = String(game.id);
            await Promise.all([
              deleteDoc(doc(db, "users", user.uid, "library", docId)),
              deleteDoc(doc(db, "users", user.uid, "completed", docId)),
            ]);
            if (game.isCustom) {
              await deleteCustomGameStorageFiles(
                user.uid,
                docId,
                game.backgroundImage,
                game.screenshots
              );
            }
          }),
        );

        const affectedGroups = groups.filter((g) =>
          g.gameIds?.some((id) => removedIds.has(String(id))),
        );

        await Promise.all(
          affectedGroups.map(async (g) => {
            const updatedIds = g.gameIds.filter(
              (id) => !removedIds.has(String(id)),
            );
            await setDoc(
              doc(db, "users", user.uid, "groups", g.id),
              { gameIds: updatedIds },
              { merge: true },
            );
          }),
        );

        setGroups((prev) =>
          prev.map((g) => ({
            ...g,
            gameIds: (g.gameIds || []).filter(
              (id) => !removedIds.has(String(id)),
            ),
          })),
        );
        setCurrentPage(1);
      }

      if (action === "unmark") {
        await Promise.all(
          gamesToApply.map(async (game) => {
            const docId = String(game.id);
            const libraryRef = doc(db, "users", user.uid, "library", docId);
            const completedRef = doc(db, "users", user.uid, "completed", docId);
            await setDoc(libraryRef, {
              ...game,
              inLibrary: true,
              status: "backlog",
              completedAt: null,
            });
            await deleteDoc(completedRef);
          }),
        );
      }

      if (!overrideGames) {
        setSelectedGames([]);
        setBulkAction("");
      }
      await loadLibrary();
    } finally {
      setIsApplying(false);
    }
  }

  async function handleRemoveFromGroup() {
    if (!groupRemovalSelected || selectedGames.length === 0) return;
    const user = getAuth().currentUser;
    if (!user) return;

    const group = groups.find((g) => g.id === groupRemovalSelected);
    if (!group) return;

    const removedIds = new Set(selectedGames.map((g) => String(g.id)));
    const updatedIds = (group.gameIds || []).filter(
      (id) => !removedIds.has(String(id)),
    );

    setIsApplying(true);
    try {
      await setDoc(
        doc(db, "users", user.uid, "groups", groupRemovalSelected),
        { gameIds: updatedIds },
        { merge: true },
      );

      setGroups((prev) =>
        prev.map((g) =>
          g.id === groupRemovalSelected ? { ...g, gameIds: updatedIds } : g,
        ),
      );

      setGroupRemovalModalOpen(false);
      setGroupRemovalSelected("");
      setSelectedGames([]);
      setBulkAction("");
    } finally {
      setIsApplying(false);
    }
  }

  async function handleAddToGroup() {
    if (!addToGroupSelected || selectedGames.length === 0) return;
    const user = getAuth().currentUser;
    if (!user) return;

    const group = groups.find((g) => g.id === addToGroupSelected);
    if (!group) return;

    const existingIds = new Set(group.gameIds || []);
    const newIds = selectedGames
      .map((g) => String(g.id))
      .filter((id) => !existingIds.has(id));

    const updatedIds = [...(group.gameIds || []), ...newIds];

    await setDoc(
      doc(db, "users", user.uid, "groups", addToGroupSelected),
      { gameIds: updatedIds },
      { merge: true },
    );

    setGroups((prev) =>
      prev.map((g) =>
        g.id === addToGroupSelected ? { ...g, gameIds: updatedIds } : g,
      ),
    );

    setAddToGroupModalOpen(false);
    setAddToGroupSelected("");
    setSelectedGames([]);
    setBulkAction("");
  }

  async function handleGroupBulkApply() {
    if (!groupBulkAction || selectedGroupRows.size === 0) return;
    const user = getAuth().currentUser;
    if (!user) return;

    if (groupBulkAction === "delete") {
      if (!window.confirm(`Delete ${selectedGroupRows.size} group(s)?`)) return;
      await Promise.all(
        [...selectedGroupRows].map((groupId) =>
          deleteDoc(doc(db, "users", user.uid, "groups", groupId)),
        ),
      );
      setGroups((prev) => {
        const updated = prev.filter((g) => !selectedGroupRows.has(g.id));
        const newTotal = Math.max(
          1,
          Math.ceil(updated.length / GROUPS_PER_PAGE),
        );
        setGroupPage((p) => Math.min(p, newTotal));
        return updated;
      });
      setSelectedGroupRows(new Set());
      setGroupBulkAction("");
    }
  }

  async function handleCreateGroupRow() {
    if (!newGroupName.trim()) return;

    const isDuplicate = groups.some(
      (g) => g.name.toLowerCase() === newGroupName.trim().toLowerCase(),
    );
    if (isDuplicate) {
      alert(`A group named "${newGroupName.trim()}" already exists.`);
      return;
    }

    const user = getAuth().currentUser;
    if (!user) return;
    setIsCreatingGroup(true);
    try {
      const savedGroup = await createGroupInFirestore(user.uid, {
        name: newGroupName.trim(),
        gameIds: [],
      });
      setGroups((prev) => [...prev, savedGroup]);
      setNewGroupName("");
    } catch (err) {
      console.error("Error creating group:", err);
    } finally {
      setIsCreatingGroup(false);
    }
  }

  async function handleDeleteGroupRow(groupId) {
    if (!window.confirm("Delete this group?")) return;
    const user = getAuth().currentUser;
    if (!user) return;
    await deleteDoc(doc(db, "users", user.uid, "groups", groupId));
    setGroups((prev) => {
      const updated = prev.filter((g) => g.id !== groupId);
      const newTotal = Math.max(1, Math.ceil(updated.length / GROUPS_PER_PAGE));
      setGroupPage((p) => Math.min(p, newTotal));
      return updated;
    });
    setSelectedGroupRows((prev) => {
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
  }

  async function handleUpdateGroupRow(groupId) {
    if (!editingGroupRowName.trim()) return;
    const user = getAuth().currentUser;
    if (!user) return;
    await setDoc(
      doc(db, "users", user.uid, "groups", groupId),
      { name: editingGroupRowName.trim() },
      { merge: true },
    );
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId ? { ...g, name: editingGroupRowName.trim() } : g,
      ),
    );
    setEditingGroupRowId(null);
    setEditingGroupRowName("");
  }

  function handleJumpInputBlur() {
    setIsJumpInputActive(false);
    const nextPage = Number(jumpPageInput);
    if (!Number.isInteger(nextPage) || nextPage < 1 || nextPage > totalPages) {
      setJumpPageInput(String(currentPage));
      return;
    }
    setCurrentPage(nextPage);
  }

  function handleGameSelect(game) {
    setSelectedGames((prev) => {
      const exists = prev.some((g) => g.id === game.id);
      const next = exists
        ? prev.filter((g) => g.id !== game.id)
        : [...prev, game];
      console.log("selectedGames:", next);
      return next;
    });
  }
  const GAMES_PER_PAGE = 12;

  const allGroupedIds = new Set(
    groups.flatMap((g) => g.gameIds?.map(String) || []),
  );

  function toggleGroupId(id) {
    setSelectedGroupIds((prev) => {
      if (id === "") return [];
      return prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id];
    });
    setCurrentPage(1);
  }

  const groupFilteredGames =
    selectedGroupIds.length === 0
      ? libInfo
      : selectedGroupIds.includes("__ungrouped__") &&
          selectedGroupIds.length === 1
        ? libInfo.filter((game) => !allGroupedIds.has(String(game.id)))
        : libInfo.filter((game) => {
            return selectedGroupIds.some((id) => {
              if (id === "__ungrouped__")
                return !allGroupedIds.has(String(game.id));
              const group = groups.find((g) => g.id === id);
              return group?.gameIds?.includes(String(game.id));
            });
          });

  const reorderGroup = groups.find((g) => g.id === reorderSelectedGroupId);
  const reorderGames = reorderGroup
    ? (reorderGroup.gameIds || [])
        .map((gid) =>
          libInfo.find((game) => String(game.id) === String(gid))
        )
        .filter(Boolean)
    : [];

  const reorderHasChanges = (() => {
    if (!reorderGroup) return false;
    const savedIds = (reorderGroup.gameIds || []).map(String);
    const currentIds = reorderList.map((g) => String(g.id));
    if (savedIds.length !== currentIds.length) return false;
    return currentIds.some((id, i) => id !== savedIds[i]);
  })();

  const filteredGames = groupFilteredGames
    .filter((game) => {
      if (statusFilter === "all") return true;
      if (statusFilter === "custom") return game.isCustom === true;
      return game.status === statusFilter;
    })
    .filter((game) => {
      if (!searchTerm.trim()) return true;
      return game.title?.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name-az":
          return (a.title || "").localeCompare(b.title || "");
        case "name-za":
          return (b.title || "").localeCompare(a.title || "");
        case "meta-high":
          return (b.metacritic || 0) - (a.metacritic || 0);
        case "meta-low":
          return (a.metacritic || 0) - (b.metacritic || 0);
        case "added-new":
          return new Date(b.addedAt || 0) - new Date(a.addedAt || 0);
        case "added-old":
          return new Date(a.addedAt || 0) - new Date(b.addedAt || 0);
        default:
          return 0;
      }
    });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredGames.length / GAMES_PER_PAGE),
  );

  const allSelected =
    filteredGames.length > 0 &&
    filteredGames.every((g) => selectedGames.some((s) => s.id === g.id));

  const totalGroupPages = Math.max(
    1,
    Math.ceil(sortedGroups.length / GROUPS_PER_PAGE),
  );
  const pagedGroups = sortedGroups.slice(
    (groupPage - 1) * GROUPS_PER_PAGE,
    groupPage * GROUPS_PER_PAGE,
  );
  const allGroupRowsSelected =
    sortedGroups.length > 0 &&
    sortedGroups.every((g) => selectedGroupRows.has(g.id));

  const filteredBeforeStatus = groupFilteredGames.filter((game) => {
    if (!searchTerm.trim()) return true;
    return game.title?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const groupStats = filteredBeforeStatus.reduce(
    (acc, game) => {
      acc.total += 1;
      if (game.status === "backlog") acc.backlog += 1;
      if (game.status === "completed") acc.completed += 1;
      if (game.status === "playing") acc.playing += 1;
      if (game.isCustom === true) acc.custom += 1;
      return acc;
    },
    { total: 0, backlog: 0, completed: 0, playing: 0, custom: 0 },
  );

  console.log(libInfo);
  useEffect(() => {
    console.log(
      "games on page:",
      filteredGames.slice(
        (currentPage - 1) * GAMES_PER_PAGE,
        currentPage * GAMES_PER_PAGE,
      ),
    );
  }, [filteredGames, currentPage]);

  useEffect(() => {
    if (!isJumpInputActive) {
      setJumpPageInput(String(currentPage));
    }
  }, [currentPage, isJumpInputActive]);

  async function loadLibrary() {
    const user = getAuth().currentUser;
    if (!user) return;

    setIsLoadingLibrary(true);
    try {
      const [librarySnap, completedSnap, groupsSnap] = await Promise.all([
        getDocs(collection(db, "users", user.uid, "library")),
        getDocs(collection(db, "users", user.uid, "completed")),
        getDocs(collection(db, "users", user.uid, "groups")),
      ]);

      const libraryDocs = librarySnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      const completedDocs = completedSnap.docs.map((d) => ({
        id: d.id,
        status: "completed",
        ...d.data(),
      }));
      const completedIds = new Set(completedDocs.map((g) => String(g.id)));
      const allDocs = [
        ...libraryDocs.filter((g) => !completedIds.has(String(g.id))),
        ...completedDocs,
      ];
      const completedCount = completedSnap.size;
      const backlogCount = libraryDocs.filter(
        (g) => g.status === "backlog",
      ).length;
      const playingCount = libraryDocs.filter(
        (g) => g.status === "playing",
      ).length;
      const customCount = allDocs.filter((g) => g.isCustom === true).length;
      const total = allDocs.length;

      setStats({
        total,
        backlog: backlogCount,
        completed: completedCount,
        playing: playingCount,
        custom: customCount,
      });
      setLibInfo(allDocs);
      setGroups(groupsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      setCurrentPage((prev) => {
        const newTotal = Math.max(
          1,
          Math.ceil(allDocs.length / GAMES_PER_PAGE),
        );
        return prev > newTotal ? newTotal : prev;
      });
    } finally {
      setIsLoadingLibrary(false);
    }
  }

  useEffect(() => {
    loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="manage-library-page">
      <div className="manage-library-page-wrapper">
        <section className="manage-library-title-row">
          <div>
            <span className="pre-header">Game Library</span>
            <h1>Manage Library</h1>
          </div>
          <Link to="/library">
            <button>← Back to Library</button>
          </Link>
        </section>
        <section className="library-stats skeleton-stats">
          <div>
            {isLoadingLibrary ? <p className="ml-skeleton ml-skeleton-stat" /> : <p>{stats.total}</p>}
            <span>Total Games</span>
          </div>
          <div>
            {isLoadingLibrary ? <span className="ml-skeleton ml-skeleton-stat" /> : <p>{stats.backlog}</p>}
            <span>Backlog</span>
          </div>
          <div>
            {isLoadingLibrary ? <span className="ml-skeleton ml-skeleton-stat" /> : <p>{stats.completed}</p>}
            <span>Completed</span>
          </div>
          <div>
            {isLoadingLibrary ? <span className="ml-skeleton ml-skeleton-stat" /> : <p>{stats.custom}</p>}
            <span>Custom</span>
          </div>
        </section>
        <section className="management-buttons">
          <button
            className={activeManagementTab === "library" ? "active" : ""}
            onClick={() => setActiveManagementTab("library")}
          >
            Game Library
          </button>
          <button
            className={activeManagementTab === "groups" ? "active" : ""}
            onClick={() => setActiveManagementTab("groups")}
          >
            Groups
          </button>
        </section>
        <section
          className="manage-library-con"
          style={{
            display: activeManagementTab === "library" ? undefined : "none",
          }}
        >
          <div className="manage-library-top">
            <div className="quick-actions">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
              >
                <option value="">Bulk Actions</option>
                <option value="complete">Mark as Complete</option>
                <option value="remove">Remove From Library</option>
                <option value="unmark">Mark as Backlog</option>
                <option value="add-to-group">Add to Group</option>
                <option value="remove-from-group">Remove From Group</option>
              </select>
              <button onClick={() => handleApply()}>Apply</button>
            </div>
            <div className="library-buttons">
              <button
                className={statusFilter === "all" ? "active" : ""}
                onClick={() => {
                  setStatusFilter("all");
                  setCurrentPage(1);
                }}
              >
                All<span>{groupStats.total}</span>
              </button>
              <button
                className={statusFilter === "backlog" ? "active" : ""}
                onClick={() => {
                  setStatusFilter("backlog");
                  setCurrentPage(1);
                }}
              >
                Backlog<span>{groupStats.backlog}</span>
              </button>
              <button
                className={statusFilter === "completed" ? "active" : ""}
                onClick={() => {
                  setStatusFilter("completed");
                  setCurrentPage(1);
                }}
              >
                Complete<span>{groupStats.completed}</span>
              </button>
              <button
                className={statusFilter === "custom" ? "active" : ""}
                onClick={() => {
                  setStatusFilter("custom");
                  setCurrentPage(1);
                }}
              >
                Custom<span>{groupStats.custom}</span>
              </button>
            </div>
            <div className="library-search">
              <div className="search-con">
                <svg viewBox="0 0 20 20" fill="none">
                  <circle
                    cx="8.5"
                    cy="8.5"
                    r="5.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                  ></circle>
                  <path
                    d="M13 13l3.5 3.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  ></path>
                </svg>
                <input
                  type="text"
                  placeholder="Search Library"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                ></input>
              </div>
              <button
                className="mobile-sort-button"
                onClick={() => setSheetView("sort")}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="4" y1="6" x2="20" y2="6"></line>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                  <line x1="11" y1="18" x2="13" y2="18"></line>
                </svg>
                <span
                  style={{
                    display:
                      (sortBy ? 1 : 0) + selectedGroupIds.length === 0
                        ? "none"
                        : undefined,
                  }}
                >
                  {(sortBy ? 1 : 0) + selectedGroupIds.length}
                </span>
              </button>
              {/* <select
                                multiple
                                value={selectedGroupIds}
                                onChange={(e) => {
                                    const selected = Array.from(e.target.selectedOptions).map((o) => o.value)
                                    setSelectedGroupIds(selected.filter((v) => v !== ""))
                                    setCurrentPage(1)
                                }}
                                size={Math.min(groups.length + 2, 6)}
                            >
                                <option value="__ungrouped__">Ungrouped</option>
                                {groups.map((g) => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </select> */}
              <div
                className={`group-trigger-con${desktopGroupTriggerOpen ? " active" : ""}`}
              >
                <div
                  className="group-trigger"
                  onClick={() => setDesktopGroupTriggerOpen((v) => !v)}
                >
                  <p>
                    {selectedGroupIds.length > 0 ? (
                      <>
                        Selected Groups <span>{selectedGroupIds.length}</span>
                      </>
                    ) : (
                      "All Groups"
                    )}
                  </p>
                </div>
                <div className="group-options">
                  <p
                    className={selectedGroupIds.length === 0 ? "active" : ""}
                    onClick={() => {
                      setSelectedGroupIds([]);
                      setCurrentPage(1);
                    }}
                  >
                    All Groups
                  </p>
                  <p
                    className={
                      selectedGroupIds.includes("__ungrouped__") ? "active" : ""
                    }
                    onClick={() => toggleGroupId("__ungrouped__")}
                  >
                    Ungrouped
                  </p>
                  {sortedGroups.map((g) => (
                    <p
                      key={g.id}
                      className={
                        selectedGroupIds.includes(g.id) ? "active" : ""
                      }
                      onClick={() => toggleGroupId(g.id)}
                    >
                      {g.name}
                    </p>
                  ))}
                </div>
              </div>
            </div>
            <div className="sort-by">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Sort By</option>
                <option value="name-az">Name (A-Z)</option>
                <option value="name-za">Name (Z-A)</option>
                <option value="meta-high">Metacritic (High-Low)</option>
                <option value="meta-low">Metacritic (Low-High)</option>
                <option value="added-new">Date Added (Newest)</option>
                <option value="added-old">Date Added (Oldest)</option>
              </select>
            </div>
            <div
              className="mobile-sort-buttons-con"
              style={{
                display:
                  isMobile && selectedGames.length > 0 ? "block" : "none",
              }}
            >
              <div className="mobile-sort-buttons-wrapper">
                <p>Bulk Actions ({selectedGames.length} selected)</p>
                <div>
                  <button
                    onClick={async () => {
                      setBulkAction("complete");
                      await handleApply("complete");
                    }}
                    disabled={selectedGames.length === 0}
                  >
                    Mark Complete
                  </button>
                  <button
                    onClick={async () => {
                      setBulkAction("remove");
                      await handleApply("remove");
                    }}
                    disabled={selectedGames.length === 0}
                  >
                    Remove From Library
                  </button>
                  <button
                    onClick={async () => {
                      setBulkAction("unmark");
                      await handleApply("unmark");
                    }}
                    disabled={selectedGames.length === 0}
                  >
                    Mark As Backlog
                  </button>
                  <button
                    onClick={() => {
                      if (selectedGames.length > 0)
                        setAddToGroupModalOpen(true);
                    }}
                    disabled={selectedGames.length === 0}
                  >
                    Add to Group
                  </button>
                  <button
                    onClick={() => {
                      if (selectedGames.length > 0)
                        setGroupRemovalModalOpen(true);
                    }}
                    disabled={selectedGames.length === 0}
                  >
                    Remove From Group
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="library-table-head">
            <div className="checkbox-con">
              <div
                className={`checkbox ${allSelected ? "active" : ""}`}
                onClick={() => {
                  if (allSelected) {
                    const filteredIds = new Set(filteredGames.map((g) => g.id));
                    setSelectedGames((prev) =>
                      prev.filter((g) => !filteredIds.has(g.id)),
                    );
                  } else {
                    setSelectedGames((prev) => {
                      const next = [...prev];
                      filteredGames.forEach((g) => {
                        if (!next.some((s) => s.id === g.id)) next.push(g);
                      });
                      return next;
                    });
                  }
                }}
              />
            </div>
            <div className="game-details-con">
              <p>Title</p>
            </div>
            <div className="genre-con">
              <p>Genre</p>
            </div>
            <div className="status-con">
              <p>Status</p>
            </div>
            <div className="platform-con">
              <p>Platforms</p>
            </div>
            <div className="score">
              <p>Score</p>
            </div>
            <div className="added-con">
              <p>Added</p>
            </div>
            <div className="groups-con">
              <p>Groups</p>
            </div>
          </div>
          <div className="library-games">
            {isLoadingLibrary
              ? Array.from({ length: GAMES_PER_PAGE }).map((_, i) => (
                  <div className="library-table-game ml-skeleton-row" key={`ml-skeleton-${i}`}>
                    <div className="checkbox-con">
                      <div className="ml-skeleton ml-skeleton-check" />
                    </div>
                    <div className="game-details-con">
                      <div className="ml-skeleton ml-skeleton-thumb" />
                      <div className="ml-skeleton-lines">
                        <span className="ml-skeleton ml-skeleton-line ml-skeleton-line-title" />
                        <span className="ml-skeleton ml-skeleton-line ml-skeleton-line-sub" />
                      </div>
                    </div>
                    <div className="genre-con"><span className="ml-skeleton ml-skeleton-line" /></div>
                    <div className="status-con"><span className="ml-skeleton ml-skeleton-pill" /></div>
                    <div className="platform-con"><span className="ml-skeleton ml-skeleton-line" /></div>
                    <div className="score"><span className="ml-skeleton ml-skeleton-line ml-skeleton-line-short" /></div>
                    <div className="added-con"><span className="ml-skeleton ml-skeleton-line" /></div>
                    <div className="groups-con"><span className="ml-skeleton ml-skeleton-pill" /></div>
                  </div>
                ))
              : filteredGames
                  .slice(
                    (currentPage - 1) * GAMES_PER_PAGE,
                    currentPage * GAMES_PER_PAGE,
                  )
                  .map((game) => (
                <div
                  className="library-table-game"
                  key={game.id}
                  onClick={() => {
                    if (window.innerWidth <= 765) {
                      setActiveSheetGame(game);
                      setSheetView("game-info");
                    }
                  }}
                >
                  <div
                    className="checkbox-con"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className={`checkbox ${selectedGames.some((s) => s.id === game.id) ? "active" : ""}`}
                      onClick={() => handleGameSelect(game)}
                    />
                  </div>
                  <div className="game-details-con">
                    <img
                      src={
                        game.backgroundImage ||
                        game.background_image ||
                        game.coverImage ||
                        noGameBackground
                      }
                    ></img>
                    <Link
                      to={gamePath(game.rawgId || game.id, game.title || game.name)}
                      className="game-details"
                    >
                      <p>{game.title}</p>
                      <div>
                        <span
                          className={`mobile-status${game.status === "completed" ? " completed" : ""}`}
                        >
                          {game.status}
                        </span>
                        <span className="mobile-rating">
                          {game.metacritic ?? "-"}
                        </span>
                        <span>
                          {typeof game.genres?.[0] === "string"
                            ? game.genres[0]
                            : game.genres?.[0]?.name || "Unlisted"}
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="genre-con">
                    <p>
                      {typeof game.genres?.[0] === "string"
                        ? game.genres[0]
                        : game.genres?.[0]?.name || "Unlisted"}
                    </p>
                  </div>
                  <div className="status-con">
                    <p
                      className={
                        game.status === "completed" ? "status-completed" : ""
                      }
                    >
                      {game.status}
                    </p>
                  </div>
                  <div className="platform-con">
                    <select>
                      {Array.isArray(game.platforms) &&
                        game.platforms.map((p, i) => (
                          <option key={i}>
                            {typeof p === "string" ? p : p.platform?.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="score">
                    <p>{game.metacritic ?? "-"}</p>
                  </div>
                  <div className="added-con">
                    <p>
                      {game.addedAt
                        ? new Date(game.addedAt).toLocaleDateString()
                        : "-"}
                    </p>
                  </div>
                  <div className="groups-con">
                    <select>
                      {groups.filter((g) =>
                        g.gameIds?.includes(String(game.id)),
                      ).length === 0 ? (
                        <option value="">Ungrouped</option>
                      ) : (
                        groups
                          .filter((g) => g.gameIds?.includes(String(game.id)))
                          .map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.name}
                            </option>
                          ))
                      )}
                    </select>
                  </div>
                  <button
                    className="mobile-options"
                    onClick={() => {
                      setActiveSheetGame(game);
                      setSheetView("game-info");
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle cx="12" cy="5" r="1"></circle>
                      <circle cx="12" cy="12" r="1"></circle>
                      <circle cx="12" cy="19" r="1"></circle>
                    </svg>
                  </button>
                </div>
              ))}
          </div>
        </section>

        <section
          className="group-management-con"
          style={{
            display: activeManagementTab === "groups" ? "flex" : "none",
          }}
        >
          <div className="group-creation-con">
            <h3>Add A New Group</h3>
            <div>
              <p>Name</p>
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateGroupRow();
                }}
                placeholder="Group name..."
              />
            </div>
            <button
              onClick={handleCreateGroupRow}
              disabled={
                !newGroupName.trim() ||
                isCreatingGroup ||
                groups.some(
                  (g) =>
                    g.name.toLowerCase() === newGroupName.trim().toLowerCase(),
                )
              }
            >
              {isCreatingGroup ? "Adding..." : "Add Group"}
            </button>
          </div>
          <div className="right-col">
            <div className="group-actions-con">
              <div>
                <select
                  value={groupBulkAction}
                  onChange={(e) => setGroupBulkAction(e.target.value)}
                >
                  <option value="">Bulk Actions</option>
                  <option value="delete">Delete</option>
                </select>
                <button
                  onClick={handleGroupBulkApply}
                  disabled={!groupBulkAction || selectedGroupRows.size === 0}
                >
                  Apply
                </button>
              </div>
              <p
                style={{
                  color: allGroupRowsSelected ? "rgb(34 197 94)" : undefined,
                  fontWeight: allGroupRowsSelected ? "bold" : undefined,
                }}
              >
                {allGroupRowsSelected ? (
                  "All Items"
                ) : (
                  <>
                    <span>{selectedGroupRows.size}</span> items
                  </>
                )}
              </p>
            </div>
            <div className="group-management">
              <div className="group-management-top">
                <div>
                  <div
                    className={`branded-check ${allGroupRowsSelected ? "active" : ""}`}
                    onClick={() => {
                      if (allGroupRowsSelected) {
                        setSelectedGroupRows(new Set());
                      } else {
                        setSelectedGroupRows(new Set(groups.map((g) => g.id)));
                      }
                    }}
                  />
                </div>
                <div>
                  <span>Name</span>
                </div>
                <div>
                  <span>Count</span>
                </div>
              </div>
              {pagedGroups.length === 0 ? (
                <p className="no-group-text">No groups yet.</p>
              ) : (
                pagedGroups.map((group) => (
                  <div key={group.id} className="group-row">
                    <div className="checkbox-con">
                      <div
                        className={`branded-check ${selectedGroupRows.has(group.id) ? "active" : ""}`}
                        onClick={() =>
                          setSelectedGroupRows((prev) => {
                            const next = new Set(prev);
                            next.has(group.id)
                              ? next.delete(group.id)
                              : next.add(group.id);
                            return next;
                          })
                        }
                      />
                    </div>
                    <div className="title-con">
                      <div
                        className="title"
                        style={{
                          display:
                            editingGroupRowId === group.id ? "none" : "flex",
                        }}
                      >
                        <p>{group.name}</p>
                        <div className="sub-actions">
                          <button
                            onClick={() => {
                              setEditingGroupRowId(group.id);
                              setEditingGroupRowName(group.name);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteGroupRow(group.id)}
                            className="delete-button"
                          >
                            Delete
                          </button>
                          <button
                            className="reorder-button"
                            onClick={() => {
                              setReorderSelectedGroupId(group.id);
                              setReorderModalOpen(true);
                            }}
                          >
                            Reorder
                          </button>
                        </div>
                      </div>
                      <div
                        className="edit-con"
                        style={{
                          display:
                            editingGroupRowId === group.id ? "flex" : "none",
                        }}
                      >
                        <input
                          type="text"
                          value={editingGroupRowName}
                          onChange={(e) =>
                            setEditingGroupRowName(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              handleUpdateGroupRow(group.id);
                          }}
                        />
                        <div>
                          <button
                            onClick={() => handleUpdateGroupRow(group.id)}
                          >
                            Update Group
                          </button>
                          <button onClick={() => setEditingGroupRowId(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="count-num-con">
                      <p>{group.gameIds?.length || 0}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="pagination-row">
              <div className="jump-to-section">
                <p>
                  Jump to page{" "}
                  <input
                    type="number"
                    value={groupPage}
                    min={1}
                    max={totalGroupPages}
                    onChange={(e) => {
                      const v = Math.min(
                        Math.max(1, Number(e.target.value)),
                        totalGroupPages,
                      );
                      if (!isNaN(v)) setGroupPage(v);
                    }}
                  />{" "}
                  of <span>{totalGroupPages}</span>
                </p>
              </div>
              <div className="group-pagination">
                <button
                  className={groupPage === 1 ? "disabled" : ""}
                  onClick={() => setGroupPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <div
                  className={`page-trigger${groupPageTriggerOpen ? " active" : ""}`}
                  onClick={() => setGroupPageTriggerOpen((v) => !v)}
                >
                  <p>
                    Page <span>{groupPage}</span> of{" "}
                    <span>{totalGroupPages}</span>
                  </p>
                  <div className="page-options">
                    {Array.from(
                      { length: totalGroupPages },
                      (_, i) => i + 1,
                    ).map((p) => (
                      <button
                        key={p}
                        className={p === groupPage ? "active" : ""}
                        onClick={() => setGroupPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  className={groupPage === totalGroupPages ? "disabled" : ""}
                  onClick={() =>
                    setGroupPage((p) => Math.min(totalGroupPages, p + 1))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </section>

        <section
          className="library-table-bottom library-management-pagnation"
          style={{
            display: activeManagementTab === "library" ? undefined : "none",
          }}
        >
          <p>
            Jump to page{" "}
            <input
              type="number"
              value={jumpPageInput}
              readOnly={!isJumpInputActive}
              onClick={() => setIsJumpInputActive(true)}
              onChange={(e) => setJumpPageInput(e.target.value)}
              onBlur={handleJumpInputBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleJumpInputBlur();
                }
              }}
            />{" "}
            of {totalPages}
          </p>
          <div className="library-table-pagination">
            <button
              disabled={isLoadingLibrary || currentPage === 1}
              style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <div className="pagn-con">
              <button
                className={`pagn-trigger${pagnOpen ? " active" : ""}`}
                disabled={isLoadingLibrary}
                onClick={() => setPagnOpen((v) => !v)}
              >
                Page <span>{currentPage}</span> of <span>{totalPages}</span>
              </button>
              <div
                className="pagn-options"
                style={{ display: pagnOpen ? undefined : "none" }}
              >
                {(() => {
                  const half = 3;
                  let start = Math.max(1, currentPage - half);
                  let end = Math.min(totalPages, start + 5);
                  if (end - start < 5) start = Math.max(1, end - 5);
                  return Array.from(
                    { length: end - start + 1 },
                    (_, i) => start + i,
                  ).map((p) => (
                    <button
                      key={p}
                      className={p === currentPage ? "active" : ""}
                      onClick={() => {
                        setCurrentPage(p);
                        setPagnOpen(false);
                      }}
                    >
                      {p}
                    </button>
                  ));
                })()}
              </div>
            </div>
            <button
              disabled={isLoadingLibrary || currentPage === totalPages}
              style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </section>
      </div>
      <div
        className="add-to-group-modal-con"
        style={{
          opacity: addToGroupModalOpen ? 1 : 0,
          pointerEvents: addToGroupModalOpen ? "all" : "none",
        }}
      >
        <div className="add-to-group-modal">
          <button
            onClick={() => {
              setAddToGroupModalOpen(false);
              setAddToGroupSelected("");
            }}
          >
            ✕
          </button>
          <h3>Which group would you like to add the selected games to?</h3>
          <select
            value={addToGroupSelected}
            onChange={(e) => setAddToGroupSelected(e.target.value)}
          >
            <option value="">Select A Group</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <button
            className={!addToGroupSelected ? "disabled" : ""}
            onClick={handleAddToGroup}
            disabled={!addToGroupSelected || selectedGames.length === 0}
          >
            Add to Group
          </button>
        </div>
      </div>
      <div
        className="loading-action-modal-con"
        style={{
          opacity: isApplying ? 1 : 0,
          pointerEvents: isApplying ? "all" : "none",
        }}
      >
        <div className="loading-action-modal">
          <img src={loadingGif} alt="Loading" />
          <h3>Action in Progress</h3>
        </div>
      </div>
      <div
        className="group-removal-modal-con"
        style={{
          opacity: groupRemovalModalOpen ? 1 : 0,
          pointerEvents: groupRemovalModalOpen ? "all" : "none",
        }}
      >
        <div className="group-removal-modal">
          <button
            onClick={() => {
              setGroupRemovalModalOpen(false);
              setGroupRemovalSelected("");
            }}
          >
            ✕
          </button>
          <h3>Which group would you like to remove the selected games from?</h3>
          <select
            value={groupRemovalSelected}
            onChange={(e) => setGroupRemovalSelected(e.target.value)}
          >
            <option value="">Select a group...</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <button
            className={!groupRemovalSelected ? "disabled" : ""}
            onClick={handleRemoveFromGroup}
            disabled={!groupRemovalSelected || selectedGames.length === 0}
          >
            Remove From Group
          </button>
        </div>
      </div>
      <div
        className={`group-lib-reorder-con${reorderModalOpen ? " open" : ""}`}
      >
        <div className="group-lib-reorder">
          <div className="title">
            <h3>Reorder Games</h3>
            <p>Drag games to set the order they appear within this group.</p>
          </div>
          <div
            ref={groupSelectionRef}
            className={`group-selection-con${groupSelectionOpen ? " open" : ""}`}
          >
            <div
              className="group-selection-trigger"
              onClick={() => setGroupSelectionOpen((v) => !v)}
            >
              <p>
                {reorderSelectedGroupId
                  ? groups.find((g) => g.id === reorderSelectedGroupId)?.name ||
                    "Select Group"
                  : "Select Group"}
              </p>
            </div>
            <div className="group-options">
              {groups.length === 0 && <p className="empty">No groups</p>}
              {groups.map((g) => (
                <p
                  key={g.id}
                  onClick={() => {
                    setReorderSelectedGroupId(g.id);
                    setGroupSelectionOpen(false);
                  }}
                >
                  {g.name}
                </p>
              ))}
            </div>
          </div>
          <div className="reorder-games-con">
            {!reorderSelectedGroupId ? (
              <p className="reorder-empty">Select a group to reorder its games.</p>
            ) : reorderList.length === 0 ? (
              <p className="reorder-empty">This group has no games yet.</p>
            ) : (
              reorderList.map((game, index) => (
                <div
                  className={
                    "reorder-game" +
                    (dragOverInfo.index === index
                      ? dragOverInfo.after
                        ? " drag-over-bottom"
                        : " drag-over-top"
                      : "")
                  }
                  key={game.id}
                  draggable
                  onDragStart={(e) => {
                    dragIndexRef.current = index;
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const after = e.clientY - rect.top > rect.height / 2;
                    setDragOverInfo({ index, after });
                  }}
                  onDragLeave={() => {
                    setDragOverInfo((prev) =>
                      prev.index === index ? { index: null, after: false } : prev
                    );
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const after = e.clientY - rect.top > rect.height / 2;
                    const target = index + (after ? 1 : 0);
                    reorderDrag(dragIndexRef.current, target);
                    dragIndexRef.current = null;
                    setDragOverInfo({ index: null, after: false });
                  }}
                  onDragEnd={() => {
                    dragIndexRef.current = null;
                    setDragOverInfo({ index: null, after: false });
                  }}
                >
                  <div>
                    <p className="count">{index + 1}</p>
                  </div>
                  <div className="grip-con">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="9" cy="6" r="1.6"></circle>
                      <circle cx="15" cy="6" r="1.6"></circle>
                      <circle cx="9" cy="12" r="1.6"></circle>
                      <circle cx="15" cy="12" r="1.6"></circle>
                      <circle cx="9" cy="18" r="1.6"></circle>
                      <circle cx="15" cy="18" r="1.6"></circle>
                    </svg>
                  </div>
                  <img
                    src={
                      game.backgroundImage ||
                      game.background_image ||
                      game.coverImage ||
                      noGameBackground
                    }
                    alt={game.title}
                  />
                  <div className="game-details">
                    <p>{game.title}</p>
                    {game.released && <p>{new Date(game.released).getFullYear()}</p>}
                  </div>
                  <div className="reorder-buttons">
                    <button onClick={() => moveReorderGame(index, -1)}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </button>
                    <button onClick={() => moveReorderGame(index, 1)}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <button
            className="close-button"
            onClick={() => setReorderModalOpen(false)}
          >
            ✕
          </button>
          <div className="reorder-save-buttons-con">
            <span className={reorderHasChanges ? "unsaved" : ""}>
              {reorderHasChanges ? "Unsaved changes" : "No changes"}
            </span>
            <div>
              <button onClick={handleCancelReorder}>Cancel</button>
              <button
                onClick={handleSaveReorder}
                disabled={!reorderHasChanges}
              >
                Save Order
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="mobile-hidden-menu"
        style={{
          opacity: sheetView !== null ? 1 : 0,
          pointerEvents: sheetView !== null ? "all" : "none",
        }}
        onClick={() => closeSheet()}
      >
        <div
          className="mobile-hidden-menu-wrapper"
          style={{
            transform:
              sheetView !== null
                ? "translate(-50%, -35%)"
                : "translate(-50%, 100%)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sheet-handle-wrapper">
            <div className="sheet-handle" onClick={() => closeSheet()}></div>
          </div>
          {sheetView === "sort" && (
            <div className="sort-options">
              <div className="title">
                <p>Sort & Filter</p>
              </div>
              <div className="option-group">
                <span>Sort By</span>
                <div className="options-wrapper">
                  <button
                    className={sortBy === "name-az" ? "active" : ""}
                    onClick={() => {
                      setSortBy("name-az");
                      setCurrentPage(1);
                    }}
                  >
                    <div>
                      <div className="icon">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="3" y1="6" x2="21" y2="6"></line>
                          <line x1="3" y1="12" x2="15" y2="12"></line>
                          <line x1="3" y1="18" x2="9" y2="18"></line>
                        </svg>
                      </div>
                      <p>Title A-Z</p>
                    </div>
                    <div
                      className="enabled-sym"
                      style={{
                        visibility: sortBy === "name-az" ? "visible" : "hidden",
                      }}
                    >
                      <span>✓</span>
                    </div>
                  </button>
                  <button
                    className={sortBy === "name-za" ? "active" : ""}
                    onClick={() => {
                      setSortBy("name-za");
                      setCurrentPage(1);
                    }}
                  >
                    <div>
                      <div className="icon">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="3" y1="6" x2="21" y2="6"></line>
                          <line x1="3" y1="12" x2="15" y2="12"></line>
                          <line x1="3" y1="18" x2="9" y2="18"></line>
                        </svg>
                      </div>
                      <p>Title Z-A</p>
                    </div>
                    <div
                      className="enabled-sym"
                      style={{
                        visibility: sortBy === "name-za" ? "visible" : "hidden",
                      }}
                    >
                      <span>✓</span>
                    </div>
                  </button>
                  <button
                    className={sortBy === "meta-high" ? "active" : ""}
                    onClick={() => {
                      setSortBy("meta-high");
                      setCurrentPage(1);
                    }}
                  >
                    <div>
                      <div className="icon">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                        </svg>
                      </div>
                      <p>Metacritic (High–Low)</p>
                    </div>
                    <div
                      className="enabled-sym"
                      style={{
                        visibility:
                          sortBy === "meta-high" ? "visible" : "hidden",
                      }}
                    >
                      <span>✓</span>
                    </div>
                  </button>
                  <button
                    className={sortBy === "meta-low" ? "active" : ""}
                    onClick={() => {
                      setSortBy("meta-low");
                      setCurrentPage(1);
                    }}
                  >
                    <div>
                      <div className="icon">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                        </svg>
                      </div>
                      <p>Metacritic (Low–High)</p>
                    </div>
                    <div
                      className="enabled-sym"
                      style={{
                        visibility:
                          sortBy === "meta-low" ? "visible" : "hidden",
                      }}
                    >
                      <span>✓</span>
                    </div>
                  </button>
                  <button
                    className={sortBy === "added-new" ? "active" : ""}
                    onClick={() => {
                      setSortBy("added-new");
                      setCurrentPage(1);
                    }}
                  >
                    <div>
                      <div className="icon">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="3"
                            y="4"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          ></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                      </div>
                      <p>Date Added (Newest)</p>
                    </div>
                    <div
                      className="enabled-sym"
                      style={{
                        visibility:
                          sortBy === "added-new" ? "visible" : "hidden",
                      }}
                    >
                      <span>✓</span>
                    </div>
                  </button>
                  <button
                    className={sortBy === "added-old" ? "active" : ""}
                    onClick={() => {
                      setSortBy("added-old");
                      setCurrentPage(1);
                    }}
                  >
                    <div>
                      <div className="icon">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="3"
                            y="4"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          ></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                      </div>
                      <p>Date Added (Oldest)</p>
                    </div>
                    <div
                      className="enabled-sym"
                      style={{
                        visibility:
                          sortBy === "added-old" ? "visible" : "hidden",
                      }}
                    >
                      <span>✓</span>
                    </div>
                  </button>
                </div>
              </div>
              <div className="option-group">
                <span>Groups</span>
                <div
                  className={`group-trigger-con${groupTriggerOpen ? " active" : ""}`}
                >
                  <div
                    className="group-trigger"
                    onClick={() => setGroupTriggerOpen((v) => !v)}
                  >
                    <p>
                      {selectedGroupIds.length > 0 ? (
                        <>
                          Selected Groups <span>{selectedGroupIds.length}</span>
                        </>
                      ) : (
                        "All Groups"
                      )}
                    </p>
                  </div>
                  <div className="group-options">
                    <p
                      className={selectedGroupIds.length === 0 ? "active" : ""}
                      onClick={() => {
                        setSelectedGroupIds([]);
                        setCurrentPage(1);
                      }}
                    >
                      All Groups
                    </p>
                    <p
                      className={
                        selectedGroupIds.includes("__ungrouped__")
                          ? "active"
                          : ""
                      }
                      onClick={() => toggleGroupId("__ungrouped__")}
                    >
                      Ungrouped
                    </p>
                    {sortedGroups.map((g) => (
                      <p
                        key={g.id}
                        className={
                          selectedGroupIds.includes(g.id) ? "active" : ""
                        }
                        onClick={() => toggleGroupId(g.id)}
                      >
                        {g.name}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {sheetView === "game-info" && activeSheetGame && (
            <div className="mobile-game-info">
              <div className="title-con">
                <div className="title">
                  <h3>{activeSheetGame.title}</h3>
                  <span>{activeSheetGame.metacritic ?? "—"}</span>
                </div>
                <div className="group-row">
                  <span>Groups</span>
                  <div>
                    {groups.filter((g) =>
                      g.gameIds?.includes(String(activeSheetGame.id)),
                    ).length === 0 ? (
                      <p>Ungrouped</p>
                    ) : (
                      groups
                        .filter((g) =>
                          g.gameIds?.includes(String(activeSheetGame.id)),
                        )
                        .map((g) => <p key={g.id}>{g.name}</p>)
                    )}
                  </div>
                </div>
                <div className="platform-row">
                  <span>Platforms</span>
                  <div>
                    {(Array.isArray(activeSheetGame.platforms)
                      ? activeSheetGame.platforms
                      : [activeSheetGame.platforms]
                    )
                      .filter(Boolean)
                      .map((p, i) => (
                        <p key={i}>
                          {typeof p === "string"
                            ? p
                            : p?.platform?.name || p?.name || ""}
                        </p>
                      ))}
                  </div>
                </div>
                <div className="change-status-con">
                  <span>Change Status</span>
                  <div
                    className="change-status-action"
                    onClick={async () => {
                      if (!activeSheetGame) return;
                      await handleApply("complete", [activeSheetGame]);
                      setActiveSheetGame((prev) => ({
                        ...prev,
                        status: "completed",
                      }));
                    }}
                  >
                    <div>
                      <div
                        className="icon"
                        style={{
                          background: "#22c55e1a",
                          borderColor: "#22c55e47",
                          color: "rgb(34, 197, 94)",
                        }}
                      >
                        ✓
                      </div>
                      <p>Mark as Complete</p>
                    </div>
                    {activeSheetGame.status === "completed" && <p>✓</p>}
                  </div>
                  <div
                    className="change-status-action"
                    onClick={async () => {
                      if (!activeSheetGame) return;
                      await handleApply("unmark", [activeSheetGame]);
                      setActiveSheetGame((prev) => ({
                        ...prev,
                        status: "backlog",
                      }));
                    }}
                  >
                    <div>
                      <div
                        className="icon"
                        style={{
                          background: "rgba(255, 255, 255, .05)",
                          border: "1px solid #94a3b81a",
                          color: "rgb(156, 163, 175)",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="size-6"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M16.5 3.75V16.5L12 14.25 7.5 16.5V3.75m9 0H18A2.25 2.25 0 0 1 20.25 6v12A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V6A2.25 2.25 0 0 1 6 3.75h1.5m9 0h-9"
                          />
                        </svg>
                      </div>
                      <p>Mark as Backlog</p>
                    </div>
                    {activeSheetGame.status === "backlog" && <p>✓</p>}
                  </div>
                </div>
                <div className="actions-con">
                  <span>Actions</span>
                  <div className="action">
                    <a
                      href={
                        activeSheetGame.isCustom
                          ? `/custom-game#${activeSheetGame.id}`
                          : gamePath(activeSheetGame.id, activeSheetGame.title || activeSheetGame.name)
                      }
                    >
                      <div
                        className="icon"
                        style={{
                          background: "rgba(255, 255, 255, .05)",
                          border: "1px solid #94a3b81a",
                          color: "rgb(156, 163, 175)",
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                      </div>
                      <p>View Game Page</p>
                    </a>
                  </div>
                  <div className="action">
                    <div
                      onClick={async () => {
                        if (!activeSheetGame) return;
                        await handleApply("remove", [activeSheetGame]);
                        closeSheet();
                      }}
                    >
                      <div
                        className="icon"
                        // style={{
                        //   background: "rgba(255, 255, 255, .05)",
                        //   border: "1px solid #94a3b81a",
                        //   color: "rgb(156, 163, 175)",
                        // }}
                      >
                        <svg
                          style={{ color: "#ef4444" }}
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                          <path d="M10 11v6M14 11v6"></path>
                        </svg>
                      </div>
                      <p style={{ color: "#ef4444" }}>Remove From Library</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
